'use client';

import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

interface StepGenderProps {
  initialValue?: 'women' | 'men';
  onNext: (gender: 'women' | 'men') => void;
}

export default function StepGender({ initialValue, onNext }: StepGenderProps) {
  const [selected, setSelected] = useState<'women' | 'men' | null>(initialValue ?? null);

  const handleNext = () => {
    if (!selected) return;
    onNext(selected);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 2xl:px-16 py-6 sm:py-10 lg:py-16">
      <div className="mb-8 sm:mb-12 animate-fade-in-up">
        <p className="text-xs font-semibold uppercase tracking-widest mb-2 sm:mb-3" style={{ color: '#C9A96E', letterSpacing: '0.12em' }}>
          Step 3 of 4
        </p>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-2 sm:mb-3" style={{ color: '#1A1A1A' }}>
          Style this look for...
        </h1>
        <p className="text-sm sm:text-base lg:text-lg" style={{ color: 'hsl(30 8% 52%)' }}>
          We&apos;ll tailor silhouettes, proportions, and pieces accordingly.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 max-w-2xl mb-8 sm:mb-12">
        {/* Women */}
        <button
          onClick={() => setSelected('women')}
          className={`
            flex-1 relative overflow-hidden rounded-3xl border-2 p-5 sm:p-8 lg:p-10 text-left transition-all duration-200 group
            ${selected === 'women' ? 'scale-[1.02]' : 'hover:scale-[1.02] hover:-translate-y-1'}
          `}
          style={{
            borderColor: selected === 'women' ? '#C9A96E' : 'hsl(35 20% 88%)',
            backgroundColor: selected === 'women' ? 'rgba(201,169,110,0.06)' : 'white',
            boxShadow: selected === 'women' ? '0 12px 40px rgba(201,169,110,0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => {
            if (selected !== 'women') (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            if (selected !== 'women') (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
          }}
          aria-pressed={selected === 'women'}
        >
          {selected === 'women' && (
            <div
              className="absolute top-3 right-3 sm:top-4 sm:right-4 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#C9A96E' }}
            >
              <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                <path d="M1 4.5L4 7.5L11 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          <div className="text-4xl sm:text-5xl mb-3 sm:mb-5">👗</div>
          <h2 className="font-serif-display text-2xl sm:text-3xl mb-1.5 sm:mb-2" style={{ color: '#1A1A1A' }}>Women</h2>
          <p className="text-xs sm:text-sm" style={{ color: 'hsl(30 8% 52%)' }}>
            Dresses, skirts, tailored trousers, feminine silhouettes, and all the accessories that complete the look.
          </p>
          <div className="mt-3 sm:mt-5 flex flex-wrap gap-1.5 sm:gap-2">
            {['Dresses', 'Blouses', 'Heels', 'Bags', 'Jewellery'].map(tag => (
              <span
                key={`women-tag-${tag}`}
                className="text-xs px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full"
                style={{ backgroundColor: 'hsl(35 15% 92%)', color: 'hsl(30 8% 52%)' }}
              >
                {tag}
              </span>
            ))}
          </div>
        </button>

        {/* Men */}
        <button
          onClick={() => setSelected('men')}
          className={`
            flex-1 relative overflow-hidden rounded-3xl border-2 p-5 sm:p-8 lg:p-10 text-left transition-all duration-200 group
            ${selected === 'men' ? 'scale-[1.02]' : 'hover:scale-[1.02] hover:-translate-y-1'}
          `}
          style={{
            borderColor: selected === 'men' ? '#C9A96E' : 'hsl(35 20% 88%)',
            backgroundColor: selected === 'men' ? 'rgba(201,169,110,0.06)' : 'white',
            boxShadow: selected === 'men' ? '0 12px 40px rgba(201,169,110,0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => {
            if (selected !== 'men') (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            if (selected !== 'men') (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
          }}
          aria-pressed={selected === 'men'}
        >
          {selected === 'men' && (
            <div
              className="absolute top-3 right-3 sm:top-4 sm:right-4 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#C9A96E' }}
            >
              <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                <path d="M1 4.5L4 7.5L11 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          <div className="text-4xl sm:text-5xl mb-3 sm:mb-5">🧥</div>
          <h2 className="font-serif-display text-2xl sm:text-3xl mb-1.5 sm:mb-2" style={{ color: '#1A1A1A' }}>Men</h2>
          <p className="text-xs sm:text-sm" style={{ color: 'hsl(30 8% 52%)' }}>
            Suits, shirts, trousers, outerwear, footwear, and the details that define a well-dressed man.
          </p>
          <div className="mt-3 sm:mt-5 flex flex-wrap gap-1.5 sm:gap-2">
            {['Suits', 'Shirts', 'Trousers', 'Shoes', 'Watches'].map(tag => (
              <span
                key={`men-tag-${tag}`}
                className="text-xs px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full"
                style={{ backgroundColor: 'hsl(35 15% 92%)', color: 'hsl(30 8% 52%)' }}
              >
                {tag}
              </span>
            ))}
          </div>
        </button>
      </div>

      <button
        onClick={handleNext}
        disabled={!selected}
        className={`
          inline-flex items-center gap-2 sm:gap-2.5 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm font-semibold
          transition-all duration-150 active:scale-95
          ${selected ? 'hover:opacity-90 hover:shadow-lg' : 'opacity-40 cursor-not-allowed'}
        `}
        style={{ backgroundColor: selected ? '#1A1A1A' : '#8A8078', color: 'white' }}
      >
        Continue to Appearance
        <ChevronRight size={16} />
      </button>
    </div>
  );
}